import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star, ShieldCheck, MapPin } from "lucide-react";

/** Google-Business-Profile-style trust badge.
 *
 * Renders: Google logo + star rating + review count + live platform stats +
 * "Verified Canadian business" chip. All stats come from /api/platform/trust
 * (real Mongo counts + env-var overrides for Google rating/reviews/profile URL).
 *
 * variant="hero"    → dark background, for use on dark hero sections
 * variant="light"   → light background, for use on register/landing content
 */

// Multi-color Google G (SVG) — the classic 4-color mark
const GoogleG = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.9 2 30.4 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.9 6.1C12.3 13.4 17.7 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.9 24.6c0-1.6-.1-3.2-.4-4.7H24v9.3h12.9c-.6 3.1-2.3 5.7-4.9 7.5l7.7 6c4.5-4.2 7.2-10.3 7.2-18.1z"/>
    <path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.7-2.9-.7-4.6s.3-3.2.7-4.6l-7.9-6.1C.9 16.3 0 20 0 24s.9 7.7 2.5 10.7l7.9-6.1z"/>
    <path fill="#34A853" d="M24 48c6.4 0 11.9-2.1 15.9-5.7l-7.7-6c-2.1 1.4-4.9 2.3-8.1 2.3-6.3 0-11.7-3.9-13.6-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
  </svg>
);

const Stars = ({ value = 5, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n}
        className={`${size} ${n <= Math.round(value) ? "fill-[#FBBC05] text-[#FBBC05]" : "text-gray-300"}`}
      />
    ))}
  </div>
);

const nice = (n) => {
  if (typeof n !== "number") return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toString();
};

export default function GoogleTrustBadge({ variant = "light" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/api/platform/trust").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const g = data.google || {};
  const p = data.platform || {};
  const isHero = variant === "hero";

  const wrapCls = isHero
    ? "bg-white/10 backdrop-blur border border-white/20 text-white"
    : "bg-white border border-[#E5E7EB] shadow-sm text-[#111827]";
  const chipCls = isHero
    ? "bg-white/10 border-white/20 text-white"
    : "bg-[#F7F5F0] border-transparent text-[#374151]";
  const muteText = isHero ? "text-white/70" : "text-[#6B7280]";

  return (
    <div className={`rounded-2xl ${wrapCls} p-4 md:p-5 flex flex-wrap items-center gap-4 md:gap-6`}
      data-testid="google-trust-badge">
      {/* Google rating */}
      <a href={g.profile_url || "#"}
        target={g.profile_url ? "_blank" : undefined}
        rel="noreferrer"
        onClick={(e) => { if (!g.profile_url) e.preventDefault(); }}
        className={`flex items-center gap-3 shrink-0 ${g.profile_url ? "hover:opacity-90 cursor-pointer" : "cursor-default"}`}
        data-testid="google-trust-rating">
        <GoogleG className="w-6 h-6" />
        <div>
          <div className={`text-[10px] uppercase tracking-wider font-semibold ${muteText}`}>Reviews on Google</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-bold text-lg leading-none">{g.rating?.toFixed(1) || "5.0"}</span>
            <Stars value={g.rating || 5.0} />
            {g.review_count > 0 && (
              <span className={`text-xs ${muteText}`}>({g.review_count})</span>
            )}
          </div>
          {g.review_count === 0 && (
            <div className={`text-[10px] ${muteText} mt-0.5`}>Freshly launched — be one of the first!</div>
          )}
        </div>
      </a>

      {/* Vertical divider */}
      <div className={`hidden md:block w-px h-12 ${isHero ? "bg-white/20" : "bg-[#E5E7EB]"}`} />

      {/* Live platform stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3" data-testid="google-trust-stats">
        <Stat label="Courses" value={nice(p.published_courses)} testid="trust-stat-courses" mute={muteText} />
        <Stat label="Learners" value={nice(p.unique_learners)} testid="trust-stat-learners" mute={muteText} />
        <Stat label="Certificates" value={nice(p.total_certificates)} testid="trust-stat-certs" mute={muteText} />
        <Stat label="Partners" value={nice(p.approved_partners)} testid="trust-stat-partners" mute={muteText} />
      </div>

      {/* Vertical divider before chips */}
      <div className={`hidden lg:block w-px h-12 ${isHero ? "bg-white/20" : "bg-[#E5E7EB]"}`} />

      {/* Verified chip */}
      <div className={`flex flex-wrap gap-2 shrink-0`}>
        <span className={`inline-flex items-center gap-1 border rounded-full px-2.5 py-1 text-[11px] font-semibold ${chipCls}`}
          data-testid="google-trust-verified-chip">
          <ShieldCheck className="w-3 h-3 text-[#34A853]" />
          Verified Canadian business
        </span>
        <span className={`inline-flex items-center gap-1 border rounded-full px-2.5 py-1 text-[11px] font-semibold ${chipCls}`}>
          <MapPin className="w-3 h-3 text-[#EA4335]" />
          Since {p.active_since_year || 2026}
        </span>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testid, mute }) => (
  <div className="text-center md:text-left" data-testid={testid}>
    <div className="font-bold text-lg leading-none">{value}</div>
    <div className={`text-[10px] uppercase tracking-wider mt-1 ${mute}`}>{label}</div>
  </div>
);
